// components/BookingConfirmation.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { getConsultationType, formatDate, formatTime } from './helpers';
import { getBudgetRange } from './helpers';

const BookingConfirmation = ({ formData, projectType, confirmationNumber }) => {
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleFinalSubmit = () => {
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <div className="text-center">
        <div className="w-20 h-20 bg-accent rounded-full flex items-center justify-center mx-auto mb-6">
          <Icon name="CheckCircle" size={40} strokeWidth={2} className="text-white" />
        </div>
        
        <h2 className="text-3xl font-bold text-primary mb-4">
          Consultation Confirmed!
        </h2>
        
        <p className="text-lg text-text-secondary mb-8 max-w-2xl mx-auto">
          Thank you for choosing The Mechanic Photography Studio.
        </p>

        <div className="bg-accent/5 border border-accent/20 rounded-xl p-6 mb-8 max-w-md mx-auto">
          <h3 className="font-bold text-primary mb-4">Confirmation Details</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-text-secondary">Confirmation #:</span>
              <span className="font-mono font-bold text-accent">{confirmationNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">Date:</span>
              <span className="font-medium text-primary">{formatDate(formData.preferredDate)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">Time:</span>
              <span className="font-medium text-primary">{formatTime(formData.preferredTime)}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
          <Link
            to="/cinematic-showcase-portfolio"
            className="flex items-center space-x-2 bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-lg font-semibold cinematic-transition cta-glow hover:shadow-elevation"
          >
            <Icon name="Play" size={20} strokeWidth={2} />
            <span>Explore Our Portfolio</span>
          </Link>
          
          <Link
            to="/homepage-cinematic-gateway"
            className="flex items-center space-x-2 border-2 border-border text-text-primary hover:bg-surface px-8 py-4 rounded-lg font-semibold cinematic-transition"
          >
            <Icon name="Home" size={20} strokeWidth={2} />
            <span>Return Home</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="text-center mb-12">
        <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-4">
          <Icon name="CheckCircle" size={16} strokeWidth={2} />
          <span>Final Review</span>
        </div>
        <h2 className="text-3xl font-bold text-primary mb-4">
          Review Your Consultation Details
        </h2>
        <p className="text-lg text-text-secondary max-w-2xl mx-auto">
          Please review all the information below.
        </p>
      </div>

      <div className="bg-surface rounded-xl p-6 mb-8">
        <h3 className="text-xl font-bold text-primary mb-6 flex items-center space-x-2">
          <Icon name={projectType?.icon || 'Briefcase'} size={20} strokeWidth={2} />
          <span>Project Summary</span>
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-text-secondary">Project Type</label>
              <p className="text-primary font-medium">{projectType?.title || 'Not specified'}</p>
            </div>
            
            <div>
              <label className="text-sm font-semibold text-text-secondary">Company</label>
              <p className="text-primary font-medium">{formData.companyName}</p>
            </div>
            
            <div>
              <label className="text-sm font-semibold text-text-secondary">Contact Person</label>
              <p className="text-primary font-medium">{formData.contactName}</p>
            </div>
            
            <div>
              <label className="text-sm font-semibold text-text-secondary">Timeline</label>
              <p className="text-primary font-medium">{formData.timeline || 'To be discussed'}</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-text-secondary">Email</label>
              <p className="text-primary font-medium">{formData.email}</p>
            </div>
            
            <div>
              <label className="text-sm font-semibold text-text-secondary">Phone</label>
              <p className="text-primary font-medium">{formData.phone}</p>
            </div>
            
            <div>
              <label className="text-sm font-semibold text-text-secondary">Budget Range</label>
              <p className="text-primary font-medium">{getBudgetRange(formData.budgetRange)}</p>
            </div>
            
            <div>
              <label className="text-sm font-semibold text-text-secondary">Industry</label>
              <p className="text-primary font-medium">{formData.industry || 'Not specified'}</p>
            </div>
          </div>
        </div>

        {formData.projectScope && (
          <div className="mt-6 pt-6 border-t border-border">
            <label className="text-sm font-semibold text-text-secondary">Project Description</label>
            <p className="text-primary mt-2 leading-relaxed">{formData.projectScope}</p>
          </div>
        )}
      </div>

      <div className="bg-accent/5 border border-accent/20 rounded-xl p-6 mb-8">
        <h3 className="text-xl font-bold text-primary mb-6 flex items-center space-x-2">
          <Icon name="Calendar" size={20} strokeWidth={2} />
          <span>Consultation Details</span>
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Icon name="Video" size={24} strokeWidth={2} className="text-accent" />
            </div>
            <h4 className="font-semibold text-primary mb-2">Format</h4>
            <p className="text-text-secondary">{getConsultationType(formData.consultationType)}</p>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Icon name="Calendar" size={24} strokeWidth={2} className="text-accent" />
            </div>
            <h4 className="font-semibold text-primary mb-2">Date</h4>
            <p className="text-text-secondary">{formatDate(formData.preferredDate)}</p>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Icon name="Clock" size={24} strokeWidth={2} className="text-accent" />
            </div>
            <h4 className="font-semibold text-primary mb-2">Time</h4>
            <p className="text-text-secondary">{formatTime(formData.preferredTime)}</p>
          </div>
        </div>
      </div>

      <div className="bg-surface rounded-xl p-6 mb-8">
        <h3 className="text-lg font-bold text-primary mb-4">
          Need to Make Changes?
        </h3>
        <p className="text-text-secondary mb-4">
          If you need to modify any details or have questions before the consultation, please contact us:
        </p>
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-6">
          <a
            href="tel:+254740000444"
            className="flex items-center space-x-2 text-accent hover:text-accent-dark font-medium cinematic-transition"
          >
            <Icon name="Phone" size={16} strokeWidth={2} />
            <span>+254 (740) 000-444</span>
          </a>
          <a
            href="mailto:info@hype.co.ke"
            className="flex items-center space-x-2 text-accent hover:text-accent-dark font-medium cinematic-transition"
          >
            <Icon name="Mail" size={16} strokeWidth={2} />
            <span>info@hype.co.ke</span>
          </a>
        </div>
      </div>

      <div className="text-center">
        <button
          onClick={handleFinalSubmit}
          className="bg-accent hover:bg-accent-dark text-white px-12 py-4 rounded-lg font-semibold text-lg cinematic-transition cta-glow hover:shadow-elevation"
        >
          Confirm Consultation Booking
        </button>
        <p className="text-sm text-text-secondary mt-4">
          By confirming, you agree to our terms and conditions.
        </p>
      </div>
    </div>
  );
};

export default BookingConfirmation;